import { useCustomerZonesList } from "../../hooks/api/v2/customerZones/useCustomerZonesList";
import {
  SupplierRelationshipListNewParameterModifierType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsType,
} from "@sellar-io/api-v2/types";
import { capitalize, formatCurrency, pluralize } from "@sellar-io/common";
import { OnChangeFn, PaginationState } from "@tanstack/react-table";
import dayjs from "dayjs";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "use-debounce";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import {
  Icon,
  ListElement,
  ProfilePicture,
  SpinnerLegacy,
} from "../../components";
import { Badge } from "../../components/badge/Badge";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { Button, DataButton, H5, IconType } from "@sellar-io/components";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { PaymentTermsIndicator } from "../../components/paymentTypeIndicator/PaymentTermsIndicator";
import { Tag } from "../../components/tag/Tag";
import { CUSTOMER, BUYERS } from "../../constants/routes";
import { useDeliveryAreaList } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { usePriceListList } from "../../hooks/api/v2/priceList/usePriceListList";
import { useSupplierRelationshipCount } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipCount";
import { useSupplierRelationshipExport } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipExport";
import { useSupplierRelationshipListNew } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipListNew";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useShowTermsAndConditionsModal } from "../../hooks/useShowTermsAndConditionsModal";
import { downloadCSV } from "../../utils/downloadHelpers";
import { TermsAndConditionsDialog } from "../settings/termsAndConditions/TermsAndConditionsDialog";
import { CustomerPaymentTermsRequestBanners } from "./Alerts/CustomerPaymentTermsRequestBanners";
import { CustomerRequestBanner } from "./Alerts/CustomerRequestBanner";
import { CustomerListFilter } from "./customerList/CustomerListFilter";

import {
  AcquiredViaFilterValues,
  CustomerDeliveryFilterValues,
  CustomerListSearchValues,
  CustomerListSortType,
  CustomerListSortValues,
  CustomerPaymentFilterType,
  CustomerPaymentFilterValues,
} from "./customerList/CustomerListFilterTypes";
import { CustomerListMap } from "./customerList/CustomerListMap";
import { useCustomerListStore } from "./customerList/CustomerListStore";
import { CustomerListTable } from "./customerList/CustomerListTable";
import { CustomerListStatePrompts } from "./CustomerListStatePrompts";
import { TableHeader } from "../../components/table/TableHeader";
import { useAvailabilityTierList } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { useOrderRulesList } from "../../hooks/api/v2/orderRules/useOrderRulesList";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { CustomersToConvertAlert } from "./customerList/CustomersToConvertAlert";
import { useNavigateToChild } from "../../hooks/useParentNavigation";

export const CustomerListNew = () => {
  const { business } = useSellarCurrentBusiness();

  if (!business?.Supplier) throw new Error("Cannot find supplier");

  const shouldShowTable = useMediaQuery("(min-width: 1200px)");

  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();
  const [isMapView, setIsMapView] = useState(false);

  const state = useCustomerListStore(useShallow((state) => state));

  const [pagination, setPaginationState] = useState<PaginationState>({
    pageIndex: state.page - 1,
    pageSize: state.perPage,
  });

  const setPagination: OnChangeFn<PaginationState> = (updater) => {
    const updated =
      typeof updater === "function" ? updater(pagination) : updater;

    setPaginationState(updated);
    state.setPage(updated.pageIndex + 1);
    state.setPerPage(updated.pageSize);
  };

  const [debouncedSearchValue] = useDebounce(state.search, 500);

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );
  const customerZonesQuery = useCustomerZonesList(
    { limit: 100 },
    { enabled: unbundlingEnabled },
  );
  const customerZones =
    customerZonesQuery.data?.pages
      .flatMap((page) => page)
      .map((zone) => ({
        ...zone,
        name:
          zone.name ||
          (zone.systemDefinedType === "sellarDistribution"
            ? "Sellar Distribution"
            : zone.default
              ? "Nationwide"
              : `Delivery zone ${zone.id}`),
      })) ?? [];

  const hasDiscounts = !unbundlingEnabled && state.hasDiscounts;

  const filtersApplied =
    (unbundlingEnabled && state.customerZoneId !== "") ||
    state.insideSellarDistributionDeliveryArea ||
    state.deliveryAreaId !== "" ||
    state.paymentTerms !== "customers" ||
    state.priceTier !== "" ||
    state.deliveryTier !== "all" ||
    state.avgOrderMin !== "" ||
    state.avgOrderMax !== "" ||
    state.lastOrderedCount !== "" ||
    state.lastOrderedUnit !== "days" ||
    hasDiscounts ||
    state.acquiredVia !== "all_channels";

  const [mobileFilter, setMobileFilter] = useState(false);
  const [desktopFilter, setDesktopFilter] = useState(false);

  const priceTierListQuery = usePriceListList(
    {
      limit: 20,
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const priceTiers =
    priceTierListQuery.data?.pages.flatMap((page) => page) ?? [];

  const hasPriceTiers = priceTiers.length > 0;

  let priceTierNames: { [key: string]: string } = {};
  if (hasPriceTiers)
    for (const tier of priceTiers) {
      priceTierNames[tier.id] = tier.name;
    }

  const availabilityTierListQuery = useAvailabilityTierList(
    {
      limit: 20,
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const availabilityTiers =
    availabilityTierListQuery.data?.pages.flatMap((page) => page) ?? [];

  const hasAvailabilityTiers = availabilityTiers.length > 0;

  let availabilityTierNames: { [key: string]: string } = {};
  if (hasAvailabilityTiers)
    for (const tier of availabilityTiers) {
      availabilityTierNames[tier.id] = tier.name;
    }

  const orderRulesListQuery = useOrderRulesList(
    {
      limit: 20,
    },
    {
      enabled: !unbundlingEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const orderRules = unbundlingEnabled
    ? []
    : (orderRulesListQuery.data?.pages.flatMap((page) => page) ?? []);

  const hasOrderRules = orderRules.length > 0;

  let orderRulesNames: { [key: string]: string } = {};
  if (hasOrderRules)
    for (const rule of orderRules) {
      orderRulesNames[rule.id] = rule.name;
    }

  const deliveryAreaListQuery = useDeliveryAreaList(
    {
      limit: 20,
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const deliveryAreas =
    deliveryAreaListQuery.data?.pages.flatMap((page) => page) ?? [];

  const { isFetched: deliveryAreasFetched } = deliveryAreaListQuery;

  const hasDeliveryAreas = deliveryAreas.length > 0;

  let deliveryAreaNames: { [key: string]: string } = {};
  if (hasDeliveryAreas)
    for (let area of deliveryAreas) {
      deliveryAreaNames[area.id] = area.name;
    }

  const handleTableSort = (columnSort: CustomerListSortType | null) => {
    state.setColumnSort(columnSort ?? "name_asc");
  };

  const buildAovQuery = ():
    | SupplierRelationshipListNewParameterModifierType
    | undefined => {
    if (!!state.avgOrderMin || !!state.avgOrderMax) {
      const avgMinNum = parseInt(state.avgOrderMin, 10);
      const avgMaxNum = parseInt(state.avgOrderMax, 10);
      return {
        type: "averageOrderValue",
        modifier: "BETWEEN",
        value: {
          low: isNaN(avgMinNum) ? 0 : avgMinNum * 100,
          high: isNaN(avgMaxNum) ? 999999999 : avgMaxNum * 100,
        },
      };
    }
    return;
  };

  const buildLastOrderedQuery = ():
    | SupplierRelationshipListNewParameterModifierType
    | undefined => {
    if (!state.lastOrderedCount || state.lastOrderedCount === "") return;
    const lastOrderedDate = dayjs()
      .subtract(parseInt(state.lastOrderedCount, 10), state.lastOrderedUnit)
      .startOf("day");
    return {
      type: "lastOrdered",
      modifier: state.lastOrderedMoreThan
        ? "LESS_THAN"
        : "GREATER_THAN_OR_EQUAL",
      value: lastOrderedDate.toDate(),
    };
  };

  const connectionQuery = [
    ...(state.insideSellarDistributionDeliveryArea
      ? ([
          {
            type: "insideSellarDistributionDeliveryArea",
            modifier: "EQUALS",
            value: true,
          },
        ] as const)
      : []),
    ...(unbundlingEnabled && state.customerZoneId
      ? ([
          {
            type: "customerZoneId",
            modifier: "EQUALS",
            value: parseInt(state.customerZoneId, 10),
          },
        ] as const)
      : []),
    ...(debouncedSearchValue
      ? CustomerListSearchValues["name"].mutation(debouncedSearchValue)
      : []),
    CustomerPaymentFilterValues[state.paymentTerms].mutation(""),
    ...(state.deliveryAreaId
      ? ([
          {
            type: "deliveryAreaId",
            modifier: "EQUALS",
            value: parseInt(state.deliveryAreaId, 10),
          },
        ] as const)
      : []),
    ...(state.priceTier
      ? ([
          {
            type: "priceTierId",
            modifier: "EQUALS",
            value: parseInt(state.priceTier, 10),
          },
        ] as const)
      : []),
    ...(state.availabilityTier
      ? ([
          {
            type: "availabilityTierId",
            modifier: "EQUALS",
            value: parseInt(state.availabilityTier, 10),
          },
        ] as const)
      : []),
    ...(!unbundlingEnabled && state.orderRule
      ? ([
          {
            type: "orderRulesId",
            modifier: "EQUALS",
            value: parseInt(state.orderRule, 10),
          },
        ] as const)
      : []),
    CustomerDeliveryFilterValues[state.deliveryTier].mutation(""),
    AcquiredViaFilterValues[state.acquiredVia].mutation(""),
    buildAovQuery(),
    buildLastOrderedQuery(),
    ...(hasDiscounts
      ? ([
          {
            type: "hasDiscounts",
            modifier: "EQUALS",
            value: hasDiscounts,
          },
        ] as const)
      : []),
  ].flatMap((x) => (x ? x : []));

  const connectionListQuery = useSupplierRelationshipListNew(
    {
      query: connectionQuery,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      sort: CustomerListSortValues[state.columnSort]
        ? [CustomerListSortValues[state.columnSort].mutation]
        : [],
    },
    {
      enabled: !isMapView,
      placeholderData: (previousData) => previousData,
      refetchOnWindowFocus: false,
    },
  );

  const connectionCountQuery = useSupplierRelationshipCount(
    {
      query: connectionQuery,
    },
    {
      placeholderData: (previousData) => previousData,
    },
  );
  const customerCount = connectionCountQuery.data ?? 0;

  const pageCount = Math.ceil(customerCount / pagination.pageSize);

  const onClickCustomer = (id: number) => {
    navigateToChild(CUSTOMER.href({ connectionId: id }));
  };

  const { data, isFetching, isLoading } = connectionListQuery;

  const unbundlingRelease2BEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const connectionWithDiscountsCountQuery = useSupplierRelationshipCount(
    {
      query: [
        {
          type: "hasDiscounts",
          modifier: "EQUALS",
          value: true,
        },
      ],
    },
    {
      enabled: unbundlingRelease2BEnabled && !unbundlingEnabled,
      placeholderData: (previousData) => previousData,
      staleTime: 5 * 60 * 1000,
    },
  );
  const customersWithDiscountsCount =
    connectionWithDiscountsCountQuery.data ?? 0;

  const [termsActive, setTermsActive] = useState(false);

  const termsAndConditionsEnabled = useShowTermsAndConditionsModal();

  const handleAddCustomerClick = () => {
    if (termsAndConditionsEnabled) {
      setTermsActive(true);
    } else {
      navigate(BUYERS);
    }
  };

  const handleTermsDismiss = (accepted: boolean) => {
    setTermsActive(false);
    if (accepted) navigate(BUYERS);
  };

  const resetPaginationOnFilterApply = () => {
    const basePerPage = 25;
    state.setPage(1);
    state.setPerPage(basePerPage);
    setPagination({
      pageIndex: 0,
      pageSize: basePerPage,
    });
  };

  const convertCustomerAlert = () => (
    <CustomersToConvertAlert
      numberOfCustomers={customersWithDiscountsCount}
      onClick={() => state.setHasDiscounts(!state.hasDiscounts)}
      active={state.hasDiscounts}
    />
  );

  return (
    <>
      <NavigationBarMenu
        title="Customers"
        borderBottom={false}
        rightComponent={
          !shouldShowTable && (
            <Button size="small" onClick={handleAddCustomerClick}>
              <Icon icon={"PLUS"} />
              Add
            </Button>
          )
        }
      ></NavigationBarMenu>

      {isLoading ? (
        <SpinnerLegacy />
      ) : isMapView ? (
        <div className={shouldShowTable ? "px-4" : ""}>
          <CustomerListHeader
            icon="FORMAT_LIST_BULLETED_SQUARE"
            buttonText="View list"
            isDesktop={shouldShowTable}
            onClick={() => setIsMapView(false)}
            className={shouldShowTable ? "" : "pb-4"}
            customerCount={customerCount}
            filterValue={state.paymentTerms}
            setFilterValue={state.setPaymentTerms}
          >
            {!!customersWithDiscountsCount &&
              unbundlingRelease2BEnabled &&
              !unbundlingEnabled &&
              convertCustomerAlert()}
          </CustomerListHeader>
          <CustomerListMap />
        </div>
      ) : (
        <div className={shouldShowTable ? "px-4" : ""}>
          <CustomerListHeader
            icon="MAP"
            buttonText="View map"
            isDesktop={shouldShowTable}
            onClick={() => setIsMapView(true)}
            customerCount={customerCount}
            filterValue={state.paymentTerms}
            setFilterValue={state.setPaymentTerms}
          >
            {!!customersWithDiscountsCount &&
              unbundlingRelease2BEnabled &&
              !unbundlingEnabled &&
              convertCustomerAlert()}
          </CustomerListHeader>

          <div className="container mx-auto mt-0 max-w-325">
            <TableHeader
              desktopFilterActive={desktopFilter}
              setDesktopFilterActive={setDesktopFilter}
              filter={
                deliveryAreasFetched &&
                hasPriceTiers && (
                  <CustomerListFilter
                    deliveryAreas={deliveryAreas}
                    customerZones={customerZones}
                    priceTiers={priceTiers}
                    availabilityTiers={availabilityTiers}
                    orderRules={orderRules}
                    onDismiss={() => {
                      setDesktopFilter(false);
                    }}
                    onApplyFilter={resetPaginationOnFilterApply}
                    filtersApplied={filtersApplied}
                  />
                )
              }
              mobileFilter={
                deliveryAreasFetched &&
                hasPriceTiers && (
                  <>
                    <div
                      className={twMerge(
                        "w-full",
                        !shouldShowTable
                          ? "px-4 pt-2"
                          : "border-b border-zinc-300 p-4",
                      )}
                    >
                      <span className="font-semibold">Sort customers</span>
                    </div>
                    <div className="w-full border-b border-zinc-300 p-4">
                      <InputContainer>
                        <InputSelect
                          value={state.columnSort}
                          onChange={(e) => {
                            state.setColumnSort(
                              e.target.value as CustomerListSortType,
                            );
                            setMobileFilter(false);
                          }}
                        >
                          {Object.values(CustomerListSortValues).map(
                            (value) => (
                              <InputOption key={value.key} value={value.key}>
                                {value.name}
                              </InputOption>
                            ),
                          )}
                        </InputSelect>
                      </InputContainer>
                    </div>
                    <CustomerListFilter
                      deliveryAreas={deliveryAreas}
                      customerZones={customerZones}
                      priceTiers={priceTiers}
                      availabilityTiers={availabilityTiers}
                      orderRules={orderRules}
                      onDismiss={() => setMobileFilter(false)}
                      onApplyFilter={resetPaginationOnFilterApply}
                      isMobile={!shouldShowTable}
                      filtersApplied={filtersApplied}
                    />
                    <div className="w-full border-y border-zinc-300 p-4">
                      <CustomerExportButton
                        color="secondary"
                        className="w-full"
                        icon="DOWNLOAD_ARROW"
                        size="large"
                      />
                    </div>
                  </>
                )
              }
              mobileFilterActive={mobileFilter}
              setMobileFilterActive={setMobileFilter}
              rightComponent={
                shouldShowTable && (
                  <div className="flex">
                    <CustomerExportButton
                      className="mr-2 px-4 py-3"
                      color="secondary"
                      icon="DOWNLOAD_ARROW"
                      size="large"
                    />
                    <Button onClick={handleAddCustomerClick} className="py-3">
                      <Icon icon={"PLUS"} />
                      Add
                    </Button>
                  </div>
                )
              }
              search={state.search}
              searchPlaceholder="Search name or postcode..."
              setSearch={(search) => {
                state.setSearch(search);
                state.setPage(1);
                setPagination({ ...pagination, pageIndex: 0 });
              }}
              tags={
                <>
                  {state.paymentTerms !== "customers" && (
                    <>
                      {/*{state.paymentTerms === "requestedPaymentTerms" ? (*/}
                      {/*  <Tag*/}
                      {/*    className="mr-2 mt-2"*/}
                      {/*    onClick={() =>*/}
                      {/*      state.setPaymentTerms("customers")*/}
                      {/*    }*/}
                      {/*    icon="TIMER_SAND_EMPTY"*/}
                      {/*  >*/}
                      {/*    Terms requested*/}
                      {/*  </Tag>*/}
                      {/*) : (*/}
                      <Tag
                        className="mt-2 mr-2"
                        onClick={() => state.setPaymentTerms("customers")}
                        icon="HANDSHAKE"
                      >
                        {state.paymentTerms === "paymentOnDelivery"
                          ? "Pay on delivery"
                          : capitalize(state.paymentTerms)}
                      </Tag>
                      {/*)}*/}
                    </>
                  )}
                  {state.priceTier !== "" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setPriceTier("")}
                      icon="TAG_OUTLINE"
                    >
                      {capitalize(priceTierNames[state.priceTier] ?? "")}
                    </Tag>
                  )}
                  {state.availabilityTier !== "" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setAvailabilityTier("")}
                      icon="BOOK_OUTLINE"
                    >
                      {capitalize(
                        availabilityTierNames[state.availabilityTier] ?? "",
                      )}
                    </Tag>
                  )}
                  {!unbundlingEnabled && state.orderRule !== "" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setOrderRule("")}
                      icon="CLIPBOARD_CHECK_OUTLINE"
                    >
                      {capitalize(orderRulesNames[state.orderRule] ?? "")}
                    </Tag>
                  )}
                  {unbundlingEnabled && state.customerZoneId !== "" && (
                    <Tag
                      className="mt-2 mr-2"
                      icon="TRUCK_OUTLINE"
                      onClick={() => {
                        state.setCustomerZoneId("");
                        resetPaginationOnFilterApply();
                      }}
                    >
                      {customerZones.find(
                        (zone) => String(zone.id) === state.customerZoneId,
                      )?.name ?? `Delivery zone ${state.customerZoneId}`}
                    </Tag>
                  )}
                  {state.insideSellarDistributionDeliveryArea && (
                    <Tag
                      className="mt-2 mr-2"
                      icon="TRUCK_OUTLINE"
                      onClick={() => {
                        state.setInsideSellarDistributionDeliveryArea(false);
                        resetPaginationOnFilterApply();
                      }}
                    >
                      Inside Sellar Distribution delivery area
                    </Tag>
                  )}
                  {state.deliveryAreaId !== "" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setDeliveryAreaId("")}
                      icon="TRUCK_OUTLINE"
                    >
                      {capitalize(
                        deliveryAreaNames[state.deliveryAreaId] ?? "",
                      )}
                    </Tag>
                  )}
                  {state.deliveryTier !== "all" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setDeliveryTier("all")}
                      icon="TRUCK_OUTLINE"
                    >
                      {capitalize(state.deliveryTier)}
                    </Tag>
                  )}
                  {(!!state.avgOrderMin || !!state.avgOrderMax) && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => {
                        state.setAvgOrderMin("");
                        state.setAvgOrderMax("");
                      }}
                      icon="CART"
                    >
                      {`Avg. order value ${state.avgOrderMin ? formatCurrency(parseInt(state.avgOrderMin, 10) * 100) : "up to"} ${state.avgOrderMax && state.avgOrderMin ? "-" : state.avgOrderMin && !state.avgOrderMax ? "+" : ""} ${state.avgOrderMax ? formatCurrency(parseInt(state.avgOrderMax, 10) * 100) : ""}`}
                    </Tag>
                  )}
                  {state.lastOrderedCount !== "" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setLastOrderedCount("")}
                      icon="CALENDAR_CLOCK"
                    >
                      {`Last ordered ${state.lastOrderedMoreThan ? "more" : "less"} than ${state.lastOrderedCount} ${state.lastOrderedUnit} ago`}
                    </Tag>
                  )}
                  {hasDiscounts && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setHasDiscounts(false)}
                      icon="PERCENT"
                    >
                      Discounts assigned
                    </Tag>
                  )}
                  {state.acquiredVia !== "all_channels" && (
                    <Tag
                      className="mt-2 mr-2"
                      onClick={() => state.setAcquiredVia("all_channels")}
                      icon="CART_OUTLINE"
                    >
                      Sellar market
                    </Tag>
                  )}
                </>
              }
            />

            {shouldShowTable ? (
              <CustomerListTable
                connectionListQuery={connectionListQuery}
                onClickCustomer={onClickCustomer}
                handleTableSort={handleTableSort}
                pagination={pagination}
                setPagination={setPagination}
                customerCount={customerCount}
              />
            ) : (
              <>
                {data &&
                  data.map((connection) => (
                    <ListElement
                      key={connection.id}
                      onClick={() => onClickCustomer(connection.id)}
                      icon={null}
                      className={!connection.approved ? "bg-zinc-100" : ""}
                    >
                      <div className={"flex w-full flex-col gap-2"}>
                        <div
                          className={
                            "flex w-full items-center gap-2 rounded-lg border border-zinc-300 bg-zinc-50 p-2"
                          }
                        >
                          <figure className="relative">
                            <ProfilePicture
                              name={connection.Retailer.Business.name}
                              logoUrl={connection.Retailer.Business.logoUrl}
                            />
                            {!connection.approved && (
                              <span className="absolute right-0 bottom-0 h-2 w-2 rounded-full border border-white bg-indigo-600" />
                            )}
                          </figure>
                          <div>
                            <H5>{connection.Retailer.Business.name}</H5>
                            <span>
                              {
                                connection.Retailer.Business.DeliveryAddress
                                  .city
                              }
                              ,{" "}
                              {
                                connection.Retailer.Business.DeliveryAddress
                                  .postalCode
                              }
                            </span>
                          </div>
                          <Icon icon="CHEVRON_RIGHT" className="ml-auto" />
                        </div>
                        <div className="flex py-2">
                          <PaymentTermsIndicator
                            connection={{
                              ...connection,
                              paymentDueCalculateFrom:
                                connection.paymentDueCalculateFrom as SupplierRelationshipPaymentDueCalculateFromType,
                              paymentTerms:
                                connection.paymentTerms as SupplierRelationshipPaymentTermsType,
                            }}
                            invoiceCreationStage={
                              business.Supplier?.invoiceCreationStage ??
                              "confirmed"
                            }
                            approved={connection.approved}
                          />
                          <Badge className="ml-2">
                            {unbundlingEnabled ? (
                              <span>
                                Default:{" "}
                                {connection.fulfillmentType ===
                                "sellarDistribution"
                                  ? "Sellar distribution"
                                  : capitalize(connection.fulfillmentType)}
                              </span>
                            ) : (
                              capitalize(connection.fulfillmentType)
                            )}
                          </Badge>
                        </div>
                        <div
                          className={
                            "grid grid-cols-[max-content_1fr] gap-x-14"
                          }
                        >
                          <H5 className="font-semibold">Price tier</H5>
                          <span>{connection.PriceTier?.name}</span>
                          <H5 className="font-semibold">Last ordered</H5>
                          <span>
                            {connection.lastOrderedAt
                              ? capitalize(
                                  dayjs(connection.lastOrderedAt).fromNow(),
                                )
                              : "Yet to order"}
                          </span>
                          <H5 className="font-semibold">Avg. order value</H5>
                          <span>
                            {formatCurrency(
                              connection.SupplierRetailerConnectionAnalytics
                                ?.averageOrderValue ?? 0,
                            )}
                          </span>
                        </div>
                      </div>
                    </ListElement>
                  ))}

                <div className="flex flex-wrap items-center gap-2 rounded-b-lg border border-t-0 border-zinc-300 bg-zinc-50 p-4">
                  <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                    <InputSelect
                      defaultValue={pagination.pageSize}
                      onChange={(e) => {
                        setPagination({
                          pageIndex: 0,
                          pageSize: parseInt(e.target.value, 10),
                        });
                      }}
                      className="text-sm font-medium"
                    >
                      {[25, 50, 75, 100].map((pageSize) => (
                        <InputOption key={pageSize} value={pageSize}>
                          {pageSize}
                        </InputOption>
                      ))}
                    </InputSelect>
                  </InputContainer>
                  <span className="mr-auto inline-block text-sm">
                    customers per page
                  </span>

                  <DataButton
                    className="p-1"
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        pageIndex: 0,
                      })
                    }
                    disabled={pagination.pageIndex === 0}
                    border={true}
                  >
                    <Icon icon="CHEVRON_DOUBLE_LEFT" />
                  </DataButton>
                  <DataButton
                    className="p-1"
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        pageIndex: pagination.pageIndex - 1,
                      })
                    }
                    disabled={pagination.pageIndex === 0}
                    border={true}
                  >
                    <Icon icon="CHEVRON_LEFT" />
                  </DataButton>

                  <span className="flex items-center gap-1 px-2">
                    <div>Page</div>
                    <strong>
                      {pagination.pageIndex + 1}{" "}
                      <span className="font-normal">of</span> {pageCount}
                    </strong>
                  </span>

                  <DataButton
                    className="p-1"
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        pageIndex: pagination.pageIndex + 1,
                      })
                    }
                    disabled={pagination.pageIndex === pageCount - 1}
                    border={true}
                  >
                    <Icon icon="CHEVRON_RIGHT" />
                  </DataButton>
                  <DataButton
                    className="p-1"
                    onClick={() =>
                      setPagination({
                        ...pagination,
                        pageIndex: pageCount - 1,
                      })
                    }
                    disabled={pagination.pageIndex === pageCount - 1}
                    border={true}
                  >
                    <Icon icon="CHEVRON_DOUBLE_RIGHT" />
                  </DataButton>
                </div>
              </>
            )}

            {!isFetching && customerCount === 0 && (
              <div className={"mt-4"}>
                <CustomerListStatePrompts
                  searchValue={state.search}
                  filterValue={
                    !state.paymentTerms || state.paymentTerms === "customers"
                      ? undefined
                      : CustomerPaymentFilterValues[state.paymentTerms].name
                  }
                  customersLength={data?.length ?? 0}
                  setFilterValue={state.setPaymentTerms}
                  setSearchValue={state.setSearch}
                  emptySearch={true}
                  callback={() => {
                    useCustomerListStore.getState().reset();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      <TermsAndConditionsDialog
        active={termsActive}
        onDismiss={handleTermsDismiss}
      />
    </>
  );
};

const CustomerListHeader = ({
  icon,
  buttonText,
  isDesktop,
  onClick,
  className,
  customerCount,
  filterValue,
  setFilterValue,
  children,
}: {
  icon: IconType;
  buttonText: string;
  isDesktop: boolean;
  onClick: () => void;
  className?: string;
  customerCount: number;
  filterValue: string;
  setFilterValue: (value: CustomerPaymentFilterType) => void;
  children?: ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div
        className={twMerge(
          "container mx-auto mt-0 flex w-full max-w-[1300px] flex-wrap items-center justify-between gap-2",
          isDesktop ? "bg-transparent py-6" : "bg-zinc-50 px-4 pt-0 pb-4",
          className,
        )}
      >
        <span className="order-2 mr-auto font-semibold text-zinc-500 md:order-1 md:mr-0">
          {customerCount} {pluralize("customer", customerCount)}
        </span>
        <div className="order-1 mr-auto flex w-full flex-col gap-2 md:order-2 md:w-auto md:flex-row">
          {filterValue !== "pending" && (
            <CustomerRequestBanner
              setFilterValue={setFilterValue}
              compact={true}
            />
          )}
          {filterValue !== "requestedPaymentTerms" && (
            <CustomerPaymentTermsRequestBanners />
          )}
        </div>
        <DataButton onClick={onClick} border={true} className="order-3 ml-auto">
          <Icon icon={icon} size={0.7} />
          {buttonText}
        </DataButton>
      </div>
      {!!children && (
        <div
          className={twMerge(
            "container mx-auto flex w-full max-w-[1300px] flex-wrap items-center justify-between gap-2",
            isDesktop
              ? "-mt-4 bg-transparent pb-4"
              : "mt-0 bg-zinc-50 px-4 pb-0",
            className,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const CustomerExportButton = ({
  className,
  color,
  icon,
  size = "small",
}: {
  className?: string;
  color?: "secondary";
  icon?: IconType;
  size?: "small" | "large";
}) => {
  const customerExportQuery = useSupplierRelationshipExport({
    enabled: false,
  });

  return (
    <Button
      className={className}
      color={color}
      loading={customerExportQuery.isFetching}
      size={size}
      onClick={async () => {
        let currentCsv = customerExportQuery.data;

        if (!currentCsv || customerExportQuery.isStale) {
          const { data: newCsv } = await customerExportQuery.refetch();
          currentCsv = newCsv;
        }

        if (currentCsv) {
          downloadCSV("sellar-customer-export.csv", currentCsv);
        }
      }}
    >
      <Icon icon={icon ?? "EXPORT"} />
      Export
    </Button>
  );
};
